import type { Project } from '../types/content'

export const projects: Project[] = [
  {
    slug: 'national-startup-ecosystem',
    title: 'Designing a national startup ecosystem',
    category: 'Startup Ecosystem Development',
    client: 'National Innovation Agency, West Africa',
    year: '2023 — 2025',
    location: 'Lagos · Accra · Abidjan',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/8dcb3393-e15e-45df-8135-22b35efcd92e.jpg',
    excerpt:
      'A three-year blueprint connecting policy, capital and founders into a single functioning market.',
    challenge:
      'A fast-growing region had capital, talent and ambition — but no connective tissue between them. Founders raised abroad, policy moved without industry, and support programmes duplicated each other.',
    approach: [
      'Mapped 400+ ecosystem actors across capital, policy, talent and infrastructure to expose the real gaps.',
      'Designed a tiered support architecture: pre-seed grants, an operator-led accelerator, and a growth-stage capital bridge.',
      'Convened a quarterly public–private council to keep regulation moving at the pace of the market.',
      'Built a shared measurement framework so every programme reported against the same outcomes.',
    ],
    outcomes: [
      { label: 'Companies onboarded', value: '180+' },
      { label: 'Follow-on capital unlocked', value: '$64M' },
      { label: 'Policy reforms adopted', value: '6' },
    ],
    quote: {
      text: 'Amoye gave us something rare — a strategy that survived contact with reality.',
      author: 'Director General, National Innovation Agency',
    },
  },
  {
    slug: 'venture-studio-build',
    title: 'Building a venture studio from zero',
    category: 'Venture Building',
    client: 'Pan-African Venture Studio',
    year: '2022 — 2024',
    location: 'Nairobi · London',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/7fa7ff0a-1a18-4c7e-b241-17c6577c3e24.jpg',
    excerpt:
      'From thesis to four operating companies — a repeatable model for building businesses in emerging markets.',
    challenge:
      'A family office wanted to build companies rather than only back them, but had no operating model, talent pipeline or governance to do it responsibly.',
    approach: [
      'Wrote the investment thesis and defined the three sectors the studio would earn the right to build in.',
      'Created the ideation-to-incorporation pipeline, including kill criteria at every gate.',
      'Recruited founding teams and structured equity so operators carried real ownership.',
      'Stood up shared services — finance, legal, design, hiring — so each company launched in weeks, not quarters.',
    ],
    outcomes: [
      { label: 'Companies launched', value: '4' },
      { label: 'Time to first revenue', value: '7 months' },
      { label: 'Combined valuation', value: '$38M' },
    ],
  },
  {
    slug: 'emerging-manager-fund',
    title: 'Standing up a first-time fund',
    category: 'Investment Advisory',
    client: 'Emerging Manager, $25M Fund I',
    year: '2024',
    location: 'London · Lagos',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/0409fb59-c170-43c3-a4c1-3d690b21d906.jpg',
    excerpt:
      'Thesis, LP narrative and investment discipline for a first-time manager entering a crowded market.',
    challenge:
      'A brilliant operator with no institutional track record needed to convince sophisticated LPs that his edge was durable, not anecdotal.',
    approach: [
      'Sharpened the thesis from a sector statement into a defensible, provable edge.',
      'Rebuilt the LP narrative around evidence: proprietary access, unfair sourcing, operator credibility.',
      'Designed the investment committee process, diligence templates and reserve strategy.',
      'Coached the founding partner through 40+ LP conversations.',
    ],
    outcomes: [
      { label: 'Fund I closed', value: '$25M' },
      { label: 'Institutional LPs', value: '9' },
      { label: 'Time to first close', value: '5 months' },
    ],
    quote: {
      text: 'She turned our pitch into a position. LPs stopped asking whether, and started asking how much.',
      author: 'Managing Partner, Fund I',
    },
  },
  {
    slug: 'founder-coaching-programme',
    title: 'A coaching practice for scaling founders',
    category: 'Founder Coaching',
    client: 'Series A & B Founders',
    year: 'Ongoing',
    location: 'Global',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/e4576b30-5584-4b93-a669-12b9ad3742cb.jpg',
    excerpt:
      'Long-form, high-trust work with founders navigating the hardest years of company building.',
    challenge:
      'Founders at Series A face a step change: the company they built is no longer the company they must run. Most advice is either tactical or therapeutic — rarely both.',
    approach: [
      'Twelve-month engagements structured around company milestones, not calendar cadence.',
      'A working agenda spanning strategy, org design, board management and personal sustainability.',
      'Direct, confidential counsel — including the conversations no one else in the company can have.',
      'Board-room preparation before every quarterly cycle.',
    ],
    outcomes: [
      { label: 'Founders coached', value: '100+' },
      { label: 'Retention into year two', value: '92%' },
      { label: 'Rounds raised during engagement', value: '31' },
    ],
  },
  {
    slug: 'corporate-innovation-unit',
    title: 'Rewiring innovation inside a bank',
    category: 'Corporate Innovation',
    client: 'Tier-One Financial Institution',
    year: '2023',
    location: 'Johannesburg',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/8dcb3393-e15e-45df-8135-22b35efcd92e.jpg',
    excerpt:
      'Turning an innovation theatre into a portfolio with real budget, real mandate and real returns.',
    challenge:
      'A well-funded innovation lab had produced 40 pilots and zero products. Enthusiasm was high; accountability was absent.',
    approach: [
      'Replaced the pilot pipeline with a venture portfolio governed by staged capital.',
      'Introduced founder-style ownership for internal teams, with real upside.',
      'Built a startup partnership pathway that cut procurement from 11 months to 6 weeks.',
      'Trained 60 executives in venture judgment and portfolio thinking.',
    ],
    outcomes: [
      { label: 'Products shipped', value: '5' },
      { label: 'Procurement time cut', value: '−87%' },
      { label: 'Executives trained', value: '60' },
    ],
  },
  {
    slug: 'public-private-partnership',
    title: 'A capital bridge between state and market',
    category: 'Public-Private Partnerships',
    client: 'Development Finance Coalition',
    year: '2022 — 2023',
    location: 'Kigali · Washington DC',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/2359222f-1ab9-4f1c-8f01-201aab6bf660.jpg',
    excerpt:
      'A blended finance vehicle that made early-stage risk investable for institutional capital.',
    challenge:
      'Development capital was abundant and slow. Venture capital was fast and scarce. Neither could fund the middle where most companies die.',
    approach: [
      'Structured a first-loss layer that made commercial participation rational.',
      'Aligned four institutions around a single set of eligibility and reporting standards.',
      'Designed a founder-facing process that took weeks rather than quarters.',
    ],
    outcomes: [
      { label: 'Vehicle size', value: '$40M' },
      { label: 'Institutions aligned', value: '4' },
      { label: 'Companies funded', value: '22' },
    ],
  },
  {
    slug: 'innovation-strategy-sprint',
    title: 'Innovation strategy for a growth market',
    category: 'Innovation Strategy',
    client: 'Global Consumer Group',
    year: '2025',
    location: 'Dubai · Lagos',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/82718273-8bfe-4d49-b261-77dfd0144320.jpg',
    excerpt:
      'A ten-year market entry thesis built with founders, not focus groups.',
    challenge:
      'A global group wanted to enter three African markets with a playbook written for Europe.',
    approach: [
      'Ran a founder-led research programme across 3 markets and 60 interviews.',
      'Rebuilt the entry model around local distribution rather than local marketing.',
      'Identified 12 partnership and acquisition targets with a staged commitment plan.',
    ],
    outcomes: [
      { label: 'Markets prioritised', value: '3' },
      { label: 'Targets identified', value: '12' },
      { label: 'Committed investment', value: '$18M' },
    ],
  },
]

export const workCategories = [
  'All',
  ...Array.from(new Set(projects.map((p) => p.category))),
]
